/**
 * Hilfsfunktionen für den Passkey-Login (WebAuthn).
 * Das Backend (Yubico java-webauthn-server) liefert und erwartet Base64url-Strings,
 * die Browser-API (navigator.credentials) arbeitet mit ArrayBuffern — hier wird konvertiert.
 */

export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined' && !!(window as any).PublicKeyCredential;
}

function base64UrlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Optionen aus startPasskeyRegistration für navigator.credentials.create() aufbereiten */
export function toCreateOptions(options: any): CredentialCreationOptions {
  const publicKey = options.publicKey;
  publicKey.challenge = base64UrlToBuffer(publicKey.challenge);
  publicKey.user.id = base64UrlToBuffer(publicKey.user.id);
  (publicKey.excludeCredentials || []).forEach(cred => cred.id = base64UrlToBuffer(cred.id));
  return {publicKey: publicKey};
}

/** Optionen aus startPasskeyLogin für navigator.credentials.get() aufbereiten */
export function toGetOptions(options: any): CredentialRequestOptions {
  const publicKey = options.publicKey;
  publicKey.challenge = base64UrlToBuffer(publicKey.challenge);
  (publicKey.allowCredentials || []).forEach(cred => cred.id = base64UrlToBuffer(cred.id));
  return {publicKey: publicKey};
}

/** Antwort von navigator.credentials.create() in das JSON-Format des Backends bringen */
export function registrationToJson(credential: any): string {
  const response = credential.response;
  return JSON.stringify({
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    response: {
      attestationObject: bufferToBase64Url(response.attestationObject),
      clientDataJSON: bufferToBase64Url(response.clientDataJSON)
    },
    clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {}
  });
}

/** Antwort von navigator.credentials.get() in das JSON-Format des Backends bringen */
export function assertionToJson(credential: any): string {
  const response = credential.response;
  return JSON.stringify({
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    response: {
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null
    },
    clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {}
  });
}
